"use client";

/**
 * A US map shaded from Authored Content.
 *
 * Each tab is an independent question with its own categories. A state is
 * highlighted when it has at least one category ticked under the active tab,
 * and hovering (or focusing, or tapping) it surfaces which ones and the detail
 * written for each.
 *
 * A tab the editor has switched off is dropped before anything renders. When
 * that leaves none, the whole section goes — but that case is decided in
 * BlockRenderer, not here; see the note on `isHidden` there.
 *
 * The geometry is a committed, pre-projected artifact — see lib/us-geo.ts.
 * Everything here is presentation over it.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { PolicyMapBlock, PolicyMapTab } from "@/lib/content";
import { visibleTabs } from "@/lib/policy-map";
import { US_STATES, US_VIEWBOX, US_STATE_NAMES } from "@/lib/us-geo";
import { Section, Eyebrow, SectionHeading } from "@/components/ui/Section";
import { clsx } from "@/lib/clsx";

const [, , VIEW_W, VIEW_H] = US_VIEWBOX.split(" ").map(Number);

interface ResolvedSelection {
  /** Carried through purely as a React key: the schema guarantees category ids
   *  are unique within a tab, but two categories may share a label. */
  id: string;
  label: string;
  detail?: string;
}

/** What the active tab says about one state, with category ids resolved to
 *  labels and ordered as the categories are ordered — not as they were ticked. */
function useTabIndex(tab: PolicyMapTab | undefined) {
  return useMemo(() => {
    const byCode = new Map<string, ResolvedSelection[]>();
    if (!tab) return byCode;
    const order = new Map(tab.categories.map((c, i) => [c.id, i]));
    const labels = new Map(tab.categories.map((c) => [c.id, c.label]));

    for (const entry of tab.states) {
      const resolved = entry.selections
        .filter((s) => labels.has(s.categoryId))
        .sort((a, b) => (order.get(a.categoryId) ?? 0) - (order.get(b.categoryId) ?? 0))
        .map((s) => ({
          id: s.categoryId,
          label: labels.get(s.categoryId)!,
          detail: s.detail || undefined,
        }));
      // No ticked categories means the editor is mid-edit; treat it exactly as
      // an unlisted state rather than highlighting an empty tooltip.
      if (resolved.length) byCode.set(entry.code, resolved);
    }
    return byCode;
  }, [tab]);
}

/**
 * Tap-to-pin exists only where the hover card cannot do the job.
 *
 * On a desktop the hover card already shows everything the pinned detail card
 * would, so making the states clickable there adds a second way to reach the
 * same content and a panel that appears under the map for no clear reason.
 *
 * Two conditions qualify, and BOTH are needed. `(hover: none)` is the touch
 * device with no pointer to hover with. `(max-width: 639px)` is the narrow
 * viewport where the hover card is `max-sm:hidden` — without this arm, a
 * hover-capable browser resized under 640px has no card and no tap, so the
 * authored notes are unreachable by every means.
 *
 * Starts false so the server render and the first client render agree.
 * Keyboard users are unaffected either way — focus raises the same card hover
 * does, and describes the focused state with it.
 */
function useTapToPin(): boolean {
  const [canPin, setCanPin] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(hover: none), (max-width: 639px)");
    const update = () => setCanPin(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return canPin;
}

export function PolicyMap({ block }: { block: PolicyMapBlock }) {
  const [tabIndex, setTabIndex] = useState(0);
  const [hovered, setHovered] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(null);
  const canPin = useTapToPin();
  // Tab ids are unique within a block, not within a page, and an editor may add
  // this block more than once. Without an instance prefix the second map's
  // aria-controls, aria-labelledby and getElementById all resolve to the first
  // map's elements, so arrow keys in one tablist move focus into the other.
  const uid = useId();
  const tabDomId = (id: string) => `${uid}tab-${id}`;
  const panelDomId = (id: string) => `${uid}panel-${id}`;
  const tipDomId = `${uid}tip`;

  // Closing the detail card unmounts the button that has focus. Without this,
  // focus falls back to <body> and the next Tab restarts from the top of the
  // document — so remember what opened the card and hand focus back to it.
  const pinTrigger = useRef<HTMLElement | null>(null);
  const pin = (next: string | null) => {
    pinTrigger.current = document.activeElement as HTMLElement | null;
    setPinned(next);
  };
  const closeCard = () => {
    setPinned(null);
    const trigger = pinTrigger.current;
    if (trigger?.isConnected) trigger.focus();
  };

  // A hidden view is not rendered and then concealed — it is absent from the
  // tablist, from the panel, and from the markup a crawler or a screen reader
  // sees, which is the only version of "hidden" worth having for content that
  // may be unfinished or wrong.
  //
  // Filtering on every render is safe and deliberately not memoised: `block` is
  // server data that never changes for the life of this component, so the array
  // is new each render but its ELEMENTS are the same objects. `useTabIndex`
  // keys off the view object, so its memo still holds.
  //
  // `tabIndex` therefore indexes the FILTERED array, and can't drift: nothing
  // in this component can hide or show a view while it is mounted.
  const tabs = visibleTabs(block.tabs);
  const tab = tabs[tabIndex] ?? tabs[0];
  const selectionsByCode = useTabIndex(tab);

  // Switching tabs must drop the pin: the pinned state very likely has nothing
  // ticked under the new question, and a detail card describing the old tab
  // while a different tab is selected is worse than no card.
  useEffect(() => {
    setPinned(null);
    setHovered(null);
  }, [tabIndex]);

  // WCAG 1.4.13 (Dismissible): the card is positioned over its neighbours, so
  // there must be a way to clear it that is not "move the pointer off". The
  // listener is on the document rather than the figure because a mouse user
  // has no focus inside the map for a React key handler to catch.
  useEffect(() => {
    if (!hovered && !pinned) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setHovered(null);
      setPinned(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [hovered, pinned]);

  if (!tab) return null;

  const active = hovered ?? pinned;
  const activeSelections = active ? selectionsByCode.get(active) : undefined;
  const pinnedSelections = pinned ? selectionsByCode.get(pinned) : undefined;

  return (
    <Section id="policy-map" tone="paper2">
      <div className="max-w-[62ch]">
        {block.eyebrow && <Eyebrow>{block.eyebrow}</Eyebrow>}
        {block.heading && <SectionHeading>{block.heading}</SectionHeading>}
        {block.intro && <p className="mt-4 text-[18px] text-ink-60">{block.intro}</p>}
      </div>

      {tabs.length > 1 && (
        /* Below `lg` the labels don't fit one row (the original four were
           ~723px of them), and flex-wrap turned them into a ragged mix of
           stacked and side-by-side buttons whose underline indicators floated
           away from the baseline — at phone widths and as a 3+1 orphan on an
           iPad. So the tablist is a vertical stack there — every tab stays
           visible (a scroll row would hide half of them) and the selected tab
           is marked by its left edge instead of an underline. */
        <div
          role="tablist"
          aria-label={block.heading || "Map views"}
          className="mt-9 flex flex-col lg:flex-row lg:flex-wrap lg:gap-1 lg:border-b border-line-strong"
        >
          {tabs.map((t, i) => (
            <button
              key={t.id}
              role="tab"
              type="button"
              id={tabDomId(t.id)}
              aria-selected={i === tabIndex}
              aria-controls={panelDomId(t.id)}
              tabIndex={i === tabIndex ? 0 : -1}
              onClick={() => setTabIndex(i)}
              onKeyDown={(e) => {
                // Down/Up mirror Right/Left because the tablist renders as a
                // vertical stack below `lg`.
                const deltas: Record<string, number> = {
                  ArrowRight: 1,
                  ArrowDown: 1,
                  ArrowLeft: -1,
                  ArrowUp: -1,
                };
                if (!(e.key in deltas)) return;
                e.preventDefault();
                const delta = deltas[e.key];
                const next = (i + delta + tabs.length) % tabs.length;
                setTabIndex(next);
                document.getElementById(tabDomId(tabs[next].id))?.focus();
              }}
              className={clsx(
                "border-l-2 py-2.5 pl-4 pr-3 text-left text-[15px] font-medium transition-colors",
                "lg:-mb-px lg:border-b-2 lg:border-l-0 lg:px-4 lg:py-3",
                i === tabIndex
                  ? "border-brass text-ink"
                  : "border-transparent text-ink-60 hover:text-ink",
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Both gates count VISIBLE views, not authored ones. A five-view map
          with four hidden must not announce a tabpanel whose labelling tab was
          never rendered: aria-labelledby would point at a missing id, and the
          panel would claim a tablist relationship with nothing in it. */}
      <div
        role={tabs.length > 1 ? "tabpanel" : undefined}
        id={panelDomId(tab.id)}
        aria-labelledby={tabs.length > 1 ? tabDomId(tab.id) : undefined}
        className="mt-8"
      >
        {tab.intro && (
          <p className="max-w-[68ch] text-[16px] leading-relaxed text-ink-60">{tab.intro}</p>
        )}

        <MapFigure
          tab={tab}
          selectionsByCode={selectionsByCode}
          active={active}
          activeSelections={activeSelections}
          tipId={tipDomId}
          onHover={setHovered}
          onPin={canPin ? (code) => pin(pinned === code ? null : code) : undefined}
          pinned={pinned}
        />

        <Legend
          neutralLabel={tab.neutralLabel || block.neutralLabel}
          count={selectionsByCode.size}
          total={US_STATES.length}
        />

        {pinned && pinnedSelections && (
          <DetailCard
            name={US_STATE_NAMES[pinned]}
            selections={pinnedSelections}
            onClose={closeCard}
          />
        )}

        <CategoryBreakdown
          tab={tab}
          selectionsByCode={selectionsByCode}
          onPick={canPin ? pin : undefined}
          picked={pinned}
        />
      </div>

      {block.footnote && (
        <p className="mt-8 font-mono text-[11px] leading-[1.6] tracking-[0.04em] text-ink-60">
          {block.footnote}
        </p>
      )}
    </Section>
  );
}

function MapFigure({
  tab,
  selectionsByCode,
  active,
  activeSelections,
  tipId,
  onHover,
  onPin,
  pinned,
}: {
  tab: PolicyMapTab;
  selectionsByCode: Map<string, ResolvedSelection[]>;
  active: string | null;
  activeSelections: ResolvedSelection[] | undefined;
  /** Id of the hover/focus card, so the active state can point at it. */
  tipId: string;
  onHover: (code: string | null) => void;
  /** Absent on hover-capable devices, where the hover card already covers it. */
  onPin?: (code: string) => void;
  pinned: string | null;
}) {
  const anchor = active ? US_STATES.find((s) => s.code === active) : undefined;

  return (
    <div className="relative mx-auto mt-7 w-full max-w-[940px]">
      {/* role="group", NOT role="img": an img role makes every child
          presentational, which would hide the individual states from assistive
          tech even though they are focusable and clickable. */}
      {/* Hover is handled ONCE here by delegation rather than per-shape.
          onMouseEnter on the highlighted paths alone left the card up when the
          pointer moved onto a neutral state or into open water, because nothing
          there fired an event to clear it. mouseover bubbles, so this sees every
          target — a highlighted state sets the card, anything else clears it. */}
      <svg
        viewBox={US_VIEWBOX}
        className="block h-auto w-full"
        role="group"
        aria-label={`${tab.label}: ${selectionsByCode.size} of ${US_STATES.length} states highlighted`}
        onMouseOver={(e) => {
          const code = (e.target as Element).getAttribute?.("data-code");
          onHover(code && selectionsByCode.has(code) ? code : null);
        }}
        onMouseLeave={() => onHover(null)}
      >
        {/* Hit corridors for the leader lines. A 0.9px stroke is not a target,
            so each connector gets an invisible 12px band along it — the pointer
            can now travel state → line → label without the card dropping out
            partway.

            ORDER IS LOAD-BEARING: this group is drawn BEFORE the states so the
            states win wherever the two overlap. Vermont's connector crosses
            Maine for half its length, and DC's crosses Maryland and Delaware;
            on top, those bands would answer "Vermont" while the pointer is
            plainly over Maine. A decorative connector must never claim a hover
            that belongs to the shape under it. Do not move this below. */}
        <g aria-hidden className="max-sm:hidden">
          {US_STATES.filter((s) => s.leader).map((state) => {
            const isActive = selectionsByCode.has(state.code);
            return (
              <line
                key={state.code}
                data-code={state.code}
                x1={state.leader![0]}
                y1={state.leader![1]}
                x2={state.cx - 5}
                y2={state.cy}
                stroke="transparent"
                strokeWidth={12}
                pointerEvents="stroke"
                className={isActive && onPin ? "cursor-pointer" : undefined}
                onClick={isActive && onPin ? () => onPin(state.code) : undefined}
              />
            );
          })}
        </g>

        {US_STATES.map((state) => {
          const selections = selectionsByCode.get(state.code);
          const isActive = !!selections;
          const isFocus = active === state.code;
          return (
            <path
              key={state.code}
              d={state.d}
              data-code={state.code}
              tabIndex={isActive ? 0 : undefined}
              // The accessible name is an ATTRIBUTE, never an SVG <title>
              // element: React 19 treats <title> as hoistable document
              // metadata, so a <title> here server-renders EMPTY and is only
              // filled in after hydration — leaving crawlers and no-JS
              // assistive tech with 51 nameless shapes.
              // A button only where it actually does something. On a desktop
              // the shape is a labelled graphic that happens to be focusable, so
              // that keyboard users get the same card hover gives.
              role={isActive ? (onPin ? "button" : "img") : undefined}
              aria-hidden={isActive ? undefined : true}
              aria-label={
                isActive
                  ? `${US_STATE_NAMES[state.code]}: ${selections
                      .map((s) => s.label)
                      .join(", ")}`
                  : undefined
              }
              // The name above carries only the category labels. The authored
              // note per category lives in the card, and a role="tooltip"
              // element nothing references is inert in ARIA — without this,
              // the notes are visible to sighted mouse users and to no one
              // else. Only the active state points at it; the card describes
              // one state at a time.
              aria-describedby={isFocus && isActive ? tipId : undefined}
              // A toggle must say whether it is on. Only meaningful where the
              // shape is a button at all, i.e. where pinning exists.
              aria-pressed={isActive && onPin ? pinned === state.code : undefined}
              className={clsx(
                "outline-none transition-[fill] duration-150",
                isActive
                  ? "fill-map-active hover:fill-map-active-hover focus-visible:fill-map-active-hover"
                  : "fill-map-neutral",
                isActive && onPin && "cursor-pointer",
                isFocus && isActive && "fill-map-active-hover",
              )}
              stroke="var(--map-stroke)"
              strokeWidth={isFocus ? 2.5 : 1}
              strokeLinejoin="round"
              onFocus={isActive ? () => onHover(state.code) : undefined}
              onBlur={isActive ? () => onHover(null) : undefined}
              onClick={isActive && onPin ? () => onPin(state.code) : undefined}
              onKeyDown={
                isActive && onPin
                  ? (e) => {
                      if (e.key !== "Enter" && e.key !== " ") return;
                      e.preventDefault();
                      onPin(state.code);
                    }
                  : undefined
              }
            />
          );
        })}

        {/* Labels sitting ON their state are decorative and let the pointer
            through to the shape underneath. They repeat a name the path already
            carries in aria-label, and go illegible below ~640px where the map
            is a third of its design width. */}
        <g aria-hidden className="pointer-events-none max-sm:hidden">
          {US_STATES.filter((s) => !s.leader).map((state) => (
            <text
              key={state.code}
              x={state.cx}
              y={state.cy}
              textAnchor="middle"
              dominantBaseline="middle"
              className={clsx(
                "font-mono text-[13px] font-medium tracking-[0.02em]",
                selectionsByCode.has(state.code) ? "fill-white" : "fill-ink-60",
              )}
            >
              {state.code}
            </text>
          ))}
        </g>

        {/* The northeastern column, which DOES take pointer events. Rhode
            Island and DC are a few pixels across at this scale — their outlines
            are not a hittable target, so the label is the only practical way to
            reach them with a mouse. */}
        <g aria-hidden className="max-sm:hidden">
          {US_STATES.filter((s) => s.leader).map((state) => {
            const isActive = selectionsByCode.has(state.code);
            const isFocus = active === state.code;
            return (
              <g key={state.code}>
                <line
                  x1={state.leader![0]}
                  y1={state.leader![1]}
                  x2={state.cx - 5}
                  y2={state.cy}
                  className="pointer-events-none"
                  stroke={isFocus ? "var(--map-active)" : "var(--line-strong)"}
                  strokeWidth={isFocus ? 1.6 : 0.9}
                />
                {/* Invisible but hit-testable box around the two characters:
                    fill="transparent" is painted (alpha 0), unlike fill="none". */}
                <rect
                  data-code={state.code}
                  x={state.cx - 5}
                  y={state.cy - 10}
                  width={34}
                  height={20}
                  fill="transparent"
                  pointerEvents="all"
                  className={isActive && onPin ? "cursor-pointer" : undefined}
                  onClick={isActive && onPin ? () => onPin(state.code) : undefined}
                />
                <text
                  x={state.cx}
                  y={state.cy}
                  textAnchor="start"
                  dominantBaseline="middle"
                  className={clsx(
                    "pointer-events-none font-mono text-[13px] tracking-[0.02em]",
                    isFocus ? "fill-map-active font-semibold" : "fill-ink-60 font-medium",
                  )}
                >
                  {state.code}
                </text>
              </g>
            );
          })}
        </g>
      </svg>

      {anchor && activeSelections && (
        <Tooltip id={tipId} anchor={anchor} selections={activeSelections} />
      )}
    </div>
  );
}

function Tooltip({
  id,
  anchor,
  selections,
}: {
  /** Referenced by the active state's aria-describedby. */
  id: string;
  anchor: { code: string; cx: number; cy: number; leader?: [number, number] };
  selections: ResolvedSelection[];
}) {
  // Anchor on the state's own label position rather than the pointer: it is
  // identical for hover, keyboard focus and tap, and it doesn't jitter as the
  // cursor crosses a state.
  const [px, py] = anchor.leader ?? [anchor.cx, anchor.cy];
  const xPct = (px / VIEW_W) * 100;
  const yPct = (py / VIEW_H) * 100;

  // Keep the card inside the figure near the edges instead of letting it
  // overflow — the northeastern states all sit within 6% of the right edge.
  const xShift = xPct > 72 ? "-92%" : xPct < 28 ? "-8%" : "-50%";
  const below = yPct < 24;

  return (
    <div
      id={id}
      role="tooltip"
      className="pointer-events-none absolute z-20 w-[min(280px,72vw)] max-sm:hidden"
      style={{
        left: `${xPct}%`,
        top: `${yPct}%`,
        transform: `translate(${xShift}, ${below ? "14px" : "calc(-100% - 14px)"})`,
      }}
    >
      <div className="rounded-sm border border-line-strong bg-card p-4 shadow-[0_8px_28px_rgba(22,32,46,0.16)]">
        <p className="font-display text-[16px] font-semibold text-ink">
          {US_STATE_NAMES[anchor.code]}
        </p>
        <ul className="mt-2.5 grid gap-2.5">
          {selections.map((s) => (
            <li key={s.id}>
              <p className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-brass-deep">
                {s.label}
              </p>
              {s.detail && (
                <p className="mt-1 text-[13.5px] leading-[1.5] text-ink-60">{s.detail}</p>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Legend({
  neutralLabel,
  count,
  total,
}: {
  neutralLabel?: string;
  count: number;
  total: number;
}) {
  return (
    <div className="mt-6 flex flex-wrap items-center justify-center gap-x-7 gap-y-2 font-mono text-[11px] uppercase tracking-[0.1em] text-ink-60">
      <span className="flex items-center gap-2">
        <span className="inline-block h-3 w-3 rounded-[2px] bg-map-active" />
        {count} of {total} states
      </span>
      <span className="flex items-center gap-2">
        <span className="inline-block h-3 w-3 rounded-[2px] border border-line-strong bg-map-neutral" />
        {neutralLabel || "No review requirement"}
      </span>
    </div>
  );
}

function DetailCard({
  name,
  selections,
  onClose,
}: {
  name: string;
  selections: ResolvedSelection[];
  onClose: () => void;
}) {
  // The card mounts most of a screen below the state that opened it. Without
  // moving focus, a tap on a state announces nothing and leaves the reader with
  // no way to find what appeared; the surrounding aria-pressed only says the
  // toggle is on, not where its output went.
  const ref = useRef<HTMLDivElement>(null);
  const headingId = useId();
  useEffect(() => {
    ref.current?.focus();
  }, [name]);

  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="group"
      aria-labelledby={headingId}
      className="mt-7 rounded-sm border border-line-strong bg-card p-5 outline-none"
    >
      <div className="flex items-start justify-between gap-4">
        <h3 id={headingId} className="font-display text-[20px] font-semibold">
          {name}
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 text-[13px] text-ink-60 underline hover:text-ink"
        >
          Close
        </button>
      </div>
      <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {selections.map((s) => (
          <div key={s.id} className="border-t border-line pt-3">
            <dt className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-brass-deep">
              {s.label}
            </dt>
            {s.detail && (
              <dd className="mt-1.5 text-[14px] leading-[1.55] text-ink-60">{s.detail}</dd>
            )}
          </div>
        ))}
      </dl>
    </div>
  );
}

/** Four across is too tight at the `md` breakpoint, so that case splits in two
 *  first. Anything beyond four falls back to the old fixed three. */
const BREAKDOWN_COLUMNS: Record<number, string> = {
  1: "md:grid-cols-1",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-2 lg:grid-cols-4",
};

/**
 * The same data as a list, grouped by category. This is the version that works
 * without a pointer, survives with JS disabled, and is what a crawler indexes —
 * a map alone would put the whole section's content out of reach of all three.
 */
function CategoryBreakdown({
  tab,
  selectionsByCode,
  onPick,
  picked,
}: {
  tab: PolicyMapTab;
  selectionsByCode: Map<string, ResolvedSelection[]>;
  /** Absent on hover-capable devices — see useTapToPin. */
  onPick?: (code: string | null) => void;
  picked: string | null;
}) {
  const groups = tab.categories
    .map((category) => ({
      id: category.id,
      label: category.label,
      codes: tab.states
        .filter(
          (s) =>
            selectionsByCode.has(s.code) &&
            s.selections.some((sel) => sel.categoryId === category.id),
        )
        .map((s) => s.code)
        .sort((a, b) => US_STATE_NAMES[a].localeCompare(US_STATE_NAMES[b])),
    }))
    .filter((g) => g.codes.length);

  if (!groups.length) return null;

  return (
    <div
      className={clsx(
        "mt-9 grid gap-6 border-t border-line-strong pt-7",
        // One column per category. A fixed three left a third of the row empty
        // on a two-category tab and orphaned the fourth group on its own row
        // above that. Enumerated rather than interpolated because Tailwind only
        // emits class names it can see in the source.
        BREAKDOWN_COLUMNS[groups.length] ?? "md:grid-cols-3",
      )}
    >
      {groups.map((group) => (
        <div key={group.id}>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.1em] text-brass-deep">
            {group.label}
          </h3>
          <p className="mt-1 text-[13px] text-ink-60">
            {group.codes.length} {group.codes.length === 1 ? "state" : "states"}
          </p>
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {group.codes.map((code) => {
              const chip = clsx(
                "block rounded-sm border px-2 py-1 font-mono text-[12px] tracking-[0.04em] transition-colors",
                picked === code
                  ? "border-map-active bg-map-active text-white"
                  : "border-line-strong bg-card text-ink",
                onPick && picked !== code && "hover:border-map-active hover:text-map-active",
              );
              // Visually a two-letter chip; to a screen reader and a crawler,
              // the state's actual name.
              const content = (
                <>
                  <span aria-hidden>{code}</span>
                  <span className="sr-only">{US_STATE_NAMES[code]}</span>
                </>
              );
              return (
                <li key={code}>
                  {/* Without a pin handler there is nothing for a click to do,
                      so this stays plain text rather than a button that looks
                      pressable and isn't. */}
                  {onPick ? (
                    <button
                      type="button"
                      aria-pressed={picked === code}
                      onClick={() => onPick(picked === code ? null : code)}
                      className={chip}
                    >
                      {content}
                    </button>
                  ) : (
                    <span className={chip}>{content}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
